/**
 * The organization's email domains, where they can be found.
 *
 * Adding one used to be possible only inside the single sign-on panel, folded
 * away under "Set up Microsoft Entra anyway", so the Organization stage asked
 * for a domain and offered nowhere to add it. Same callable, same rules: a
 * domain claimed by another organization is refused, and sign-in routing reads
 * VERIFIED domains only — by a DNS record they add, or on our own authority
 * when we know the domain is theirs.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../../lib/callable';
import page from '../../../shared/Page.module.css';
import form from '../../../shared/Form.module.css';
import { GroupCard } from '../../../shared/GroupCard';

type Action = 'add' | 'remove' | 'verify' | 'trust';
interface Result {
  success: boolean; verified?: boolean; message?: string;
  dnsRecord?: { type?: string; host: string; value: string };
}

const DOMAIN_RE = /^(?=.{3,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** "@Mercy.org " and "https://mercy.org/" both mean mercy.org. */
export function normalizeDomain(input: string): string {
  return input.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^.*@/, '').replace(/\/.*$/, '');
}

export function DomainsPanel({ orgId, domains }: { orgId: string; domains: { domain: string; verified: boolean }[] }) {
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [removing, setRemoving] = useState<string | null>(null);
  const [note, setNote] = useState<{ domain: string; text: string; record?: Result['dnsRecord'] } | null>(null);

  const run = useMutation({
    mutationFn: (v: { domain: string; action: Action }) =>
      call<{ orgId: string; domain: string; action: Action }, Result>('sysAdminManageOrgDomain', { orgId, ...v }),
    onSuccess: (r, v) => {
      if (v.action === 'add') {
        setInput('');
        setNote({ domain: v.domain, text: r.verified ? 'Added. It was already verified.' : 'Added, not verified yet.', record: r.dnsRecord });
      } else if (v.action === 'verify') {
        setNote({ domain: v.domain, text: r.verified ? 'Verified by its DNS record.' : 'The DNS record was not found yet. It can take a few hours to appear.' });
      } else if (v.action === 'trust') {
        setNote({ domain: v.domain, text: 'Marked verified.' });
      } else {
        setRemoving(null); setNote(null);
      }
      void queryClient.invalidateQueries({ queryKey: ['onboarding', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding-activity', orgId] });
    },
  });

  const candidate = normalizeDomain(input);
  const valid = DOMAIN_RE.test(candidate);
  const taken = domains.some((d) => d.domain === candidate);

  return (
    <GroupCard id="org-domains-title" title="Email domains" focusable>
      <p className={page.itemMeta} style={{ maxWidth: '72ch' }}>
        The part of their staff&apos;s addresses after the @. Sign-in finds this organization from it,
        and only verified domains count: they add a DNS record, or you mark it verified when you
        know the domain is theirs.
      </p>

      {domains.length > 0 && (
        <ul className={page.list} aria-label="Email domains" style={{ marginTop: 'var(--sp-3)' }}>
          {domains.map((d) => (
            <li key={d.domain} className={page.listItem}>
              <div>
                <span className={page.itemName} style={{ fontFamily: 'var(--font-mono)' }}>{d.domain}</span>{' '}
                <span className={`${page.tag} ${d.verified ? page.ok : page.warn}`}>{d.verified ? 'verified' : 'not verified'}</span>
              </div>
              <div className={page.actions}>
                {!d.verified && (
                  <>
                    <button type="button" className={page.btn} disabled={run.isPending}
                      onClick={() => run.mutate({ domain: d.domain, action: 'verify' })}>Check DNS</button>
                    <button type="button" className={page.btn} disabled={run.isPending}
                      onClick={() => run.mutate({ domain: d.domain, action: 'trust' })}>Mark verified</button>
                  </>
                )}
                {removing === d.domain ? (
                  <>
                    <button type="button" className={`${page.btn} ${page.dangerText}`} disabled={run.isPending}
                      onClick={() => run.mutate({ domain: d.domain, action: 'remove' })}>Remove {d.domain}</button>
                    <button type="button" className={page.btn} onClick={() => setRemoving(null)}>Keep</button>
                  </>
                ) : (
                  <button type="button" className={page.btn} disabled={run.isPending} onClick={() => setRemoving(d.domain)}>Remove</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className={form.row} style={{ marginTop: 'var(--sp-3)', alignItems: 'end', gridTemplateColumns: 'minmax(0, 320px) auto' }}>
        <div>
          <label className={form.label} htmlFor="org-domain-new">{domains.length > 0 ? 'Add another domain' : 'Add a domain'}</label>
          <input id="org-domain-new" className={form.input} maxLength={253} placeholder="hospital.org"
            value={input} aria-invalid={(input.trim() !== '' && !valid) || undefined}
            onChange={(e) => setInput(e.target.value)}
            aria-describedby="org-domain-hint"
            onKeyDown={(e) => { if (e.key === 'Enter' && valid && !taken) run.mutate({ domain: candidate, action: 'add' }); }} />
        </div>
        <div>
          <button type="button" className={`${page.btn} ${page.btnPrimary}`} disabled={!valid || taken || run.isPending}
            onClick={() => run.mutate({ domain: candidate, action: 'add' })}>
            {run.isPending && run.variables?.action === 'add' ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
      <p className={form.hint} id="org-domain-hint">
        An organization can have several, such as a hospital and its clinics. Each one you add
        joins the list above.
      </p>
      {input.trim() !== '' && !valid && <p className={form.error}>That is not a domain. Enter just the part after the @, such as their main website address.</p>}
      {taken && <p className={form.hint}>{candidate} is already on the list.</p>}

      {run.error && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert" style={{ marginTop: 'var(--sp-3)' }}>
          {isCallFailure(run.error) ? run.error.message : 'That did not work.'}
        </div>
      )}
      {note && (
        <div className={`${page.notice} ${page.noticeInfo}`} role="status" style={{ marginTop: 'var(--sp-3)' }}>
          <strong>{note.domain}:</strong> {note.text}
          {note.record && (
            <> To verify it by DNS, their IT adds a TXT record: host{' '}
              <span style={{ fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }}>{note.record.host}</span>, value{' '}
              <span style={{ fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }}>{note.record.value}</span>.</>
          )}
        </div>
      )}
    </GroupCard>
  );
}
